import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  NOTIFICATION_TYPE_LABELS,
  type NotificationType,
} from '../../notificationTaxonomy';

interface Notification {
  id: string;
  notificationType: NotificationType;
  title: string;
  message: string;
  relatedOfficial?: {
    id: string;
    name: string;
    photoUrl?: string | null;
  } | null;
  relatedContributionId?: string;
  actionUrl?: string;
  isRead: boolean;
  createdAt: number;
}

interface NotificationCenterProps {
  compact?: boolean;
  maxItems?: number;
}

const notificationIcons: Record<NotificationType, React.ReactNode> = {
  adoption_update: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v12m6-6H6" />
    </svg>
  ),
  new_intelligence: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  community_contribution: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  accountability_record: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  ),
  social_activity: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
    </svg>
  ),
  system: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

const notificationColors: Record<NotificationType, string> = {
  adoption_update: 'border-red-500/20 bg-red-500/10 text-red-500',
  new_intelligence: 'border-blue-500/20 bg-blue-500/10 text-blue-500',
  community_contribution: 'border-purple-500/20 bg-purple-500/10 text-purple-500',
  accountability_record: 'border-amber-500/20 bg-amber-500/10 text-amber-500',
  social_activity: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-500',
  system: 'border-navy-500/20 bg-navy-500/10 text-navy-500 dark:text-navy-300',
};

function timeAgo(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

export default function NotificationCenter({ compact = false, maxItems }: NotificationCenterProps) {
  const { token, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchNotifications() {
      if (!isAuthenticated || !token) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch('/api/notifications/me', {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch notifications');
        }

        const data = (await response.json()) as { notifications?: Notification[] };
        setNotifications(data.notifications || []);
      } catch (err: any) {
        setError(err.message || 'Failed to load notifications');
      } finally {
        setLoading(false);
      }
    }

    void fetchNotifications();
  }, [token, isAuthenticated]);

  const markAsRead = async (notificationId: string) => {
    if (!token) return;

    try {
      const response = await fetch(`/api/notifications/${notificationId}/read`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        setNotifications((prev) =>
          prev.map((notification) =>
            notification.id === notificationId ? { ...notification, isRead: true } : notification,
          ),
        );
      }
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    if (!token) return;

    try {
      const response = await fetch('/api/notifications/read-all', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        setNotifications((prev) => prev.map((notification) => ({ ...notification, isRead: true })));
      }
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="rounded-xl border border-cream-300 bg-white p-8 text-center dark:border-navy-600 dark:bg-navy-800/40">
        <svg className="mx-auto mb-4 h-12 w-12 text-navy-400 dark:text-navy-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        <p className="text-sm font-bold text-navy-700 dark:text-cream-200">Login to view alerts</p>
      </div>
    );
  }

  const filteredNotifications = filter === 'unread'
    ? notifications.filter((notification) => !notification.isRead)
    : notifications;

  const displayedNotifications = maxItems
    ? filteredNotifications.slice(0, maxItems)
    : filteredNotifications;

  const unreadCount = notifications.filter((notification) => !notification.isRead).length;

  return (
    <div className="overflow-hidden rounded-xl border border-cream-300 bg-white dark:border-navy-600 dark:bg-navy-800/40">
      <div className="border-b border-cream-300 bg-cream-50 px-5 py-4 dark:border-navy-600 dark:bg-navy-800/20">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold text-navy-900 dark:text-cream-50">Alerts</h3>
            {unreadCount > 0 && (
              <span className="rounded-full bg-red-500 px-2 py-0.5 text-[9px] font-bold text-white">
                {unreadCount}
              </span>
            )}
          </div>
          {!compact && unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-[10px] font-bold uppercase tracking-wider text-blue-600 transition hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Mark all read
            </button>
          )}
        </div>

        {!compact && (
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`rounded px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition ${
                filter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-cream-200 text-navy-600 hover:bg-cream-300 dark:bg-navy-700 dark:text-cream-300 dark:hover:bg-navy-600'
              }`}
            >
              All ({notifications.length})
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`rounded px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition ${
                filter === 'unread'
                  ? 'bg-blue-600 text-white'
                  : 'bg-cream-200 text-navy-600 hover:bg-cream-300 dark:bg-navy-700 dark:text-cream-300 dark:hover:bg-navy-600'
              }`}
            >
              Unread ({unreadCount})
            </button>
          </div>
        )}
      </div>

      <div className="divide-y divide-cream-200 dark:divide-navy-600/40">
        {loading ? (
          <div className="p-6 text-center text-sm text-navy-500 dark:text-cream-300">Loading alerts...</div>
        ) : error ? (
          <div className="p-6 text-center text-sm text-red-500">{error}</div>
        ) : displayedNotifications.length === 0 ? (
          <div className="p-6 text-center text-sm text-navy-500 dark:text-cream-300">
            No alerts in this view.
          </div>
        ) : (
          displayedNotifications.map((notification) => {
            const actionHref = notification.actionUrl || (notification.relatedOfficial ? `/officials/${notification.relatedOfficial.id}` : undefined);

            return (
              <div key={notification.id} className="p-5 transition hover:bg-cream-50/70 dark:hover:bg-navy-800/30">
                <div className="flex items-start gap-4">
                  <div className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border ${notificationColors[notification.notificationType]}`}>
                    {notificationIcons[notification.notificationType]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-center gap-2">
                      <span className="text-[10px] font-black uppercase tracking-widest text-gold-600 dark:text-gold-400">
                        {NOTIFICATION_TYPE_LABELS[notification.notificationType]}
                      </span>
                      {!notification.isRead && (
                        <span className="rounded-full bg-gold-500 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-navy-900">
                          New
                        </span>
                      )}
                    </div>
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <h4 className="text-sm font-bold text-navy-900 dark:text-cream-50">{notification.title}</h4>
                      <span className="text-xs text-navy-400 dark:text-navy-300">{timeAgo(notification.createdAt)}</span>
                    </div>
                    <p className="text-sm text-navy-600 dark:text-cream-300">{notification.message}</p>
                    {notification.relatedOfficial && (
                      <p className="mt-2 text-xs font-bold uppercase tracking-wider text-navy-400 dark:text-navy-300">
                        {notification.relatedOfficial.name}
                      </p>
                    )}
                    <div className="mt-3 flex flex-wrap gap-3">
                      {actionHref && (
                        <Link
                          to={actionHref}
                          onClick={() => {
                            if (!notification.isRead) {
                              void markAsRead(notification.id);
                            }
                          }}
                          className="text-xs font-bold uppercase tracking-wider text-blue-600 transition hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          Open
                        </Link>
                      )}
                      {!notification.isRead && (
                        <button
                          onClick={() => void markAsRead(notification.id)}
                          className="text-xs font-bold uppercase tracking-wider text-navy-500 transition hover:text-navy-700 dark:text-cream-300 dark:hover:text-cream-100"
                        >
                          Mark Read
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
